// Formatters and payload helpers shared by the investigation page and its
// panels. Everything here is pure: the endpoint's own field names go in, a
// display string comes out.

const EM_DASH = '–'

export function formatDuration(milliseconds) {
  if (milliseconds == null) return EM_DASH
  if (milliseconds < 1000) return `${milliseconds} ms`
  return `${(milliseconds / 1000).toFixed(milliseconds >= 10000 ? 1 : 2)} s`
}

export function formatTimestamp(value) {
  if (!value) return EM_DASH
  const date = new Date(value)
  if (isNaN(date.getTime())) return value
  return date.toLocaleString('nl-NL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatBoolean(value) {
  if (value === true) return 'Ja'
  if (value === false) return 'Nee'
  return EM_DASH
}

// The endpoint hands dates back ISO-first; the app shows them day-first.
export function formatDate(value) {
  if (!value) return EM_DASH
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value
}

export function formatEuro(value) {
  if (value == null) return EM_DASH
  const amount = Number(value).toLocaleString('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `€\u00a0${amount}`
}

export function formatNumber(value) {
  if (value == null) return EM_DASH
  return Number(value).toLocaleString('nl-NL')
}

// LIB returns a week as yyyyww; the design reads it back as "ww · yyyy".
export function formatWeek(value) {
  if (!value) return EM_DASH
  return `${value.slice(4)} · ${value.slice(0, 4)}`
}

// STAGE_NAME -> "Stage name", for the places a stage is prose rather than a code.
export function formatStage(stage) {
  return (stage || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^./, (character) => character.toUpperCase())
}

// A call failed if it said so, or if the status code says so for it.
export function isFailedCall(call) {
  return !!call.failed || call.httpStatus >= 400
}

// Bodies arrive as strings. JSON is re-indented for reading; anything else is
// shown exactly as it came over the wire.
export function prettyPrintBody(text) {
  try {
    return { text: JSON.stringify(JSON.parse(text), null, 2), isJson: true }
  } catch {
    return { text, isJson: false }
  }
}

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;' }

function escapeHtml(text) {
  return text.replace(/[&<>]/g, (character) => HTML_ESCAPES[character])
}

// Minimal JSON colouring: escape first, then wrap the four token kinds in the
// .ds-code spans. Deliberately one regex pass and no library — the payloads are
// already large, and this only ever runs on a body the user expanded.
//
// The result is injected with v-html, which is safe precisely because the
// escaping happens before any span is added: nothing from the payload can
// close a tag.
export function highlightJson(text) {
  return escapeHtml(text).replace(
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g,
    (match, string, colon, literal, number) => {
      if (string) {
        return colon
          ? `<span class="ds-code__key">${string}</span>${colon}`
          : `<span class="ds-code__string">${string}</span>`
      }
      if (literal) return `<span class="ds-code__boolean">${literal}</span>`
      return `<span class="ds-code__number">${number}</span>`
    }
  )
}

// The clipboard API is not always available (an iframe without the permission,
// a non-secure context), so fall back to the textarea + execCommand route.
export function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard
      .writeText(text)
      .then(() => true)
      .catch(() => legacyCopy(text))
  }
  return Promise.resolve(legacyCopy(text))
}

function legacyCopy(text) {
  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.cssText = 'position:fixed;top:0;left:-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(textarea)
    return copied
  } catch {
    return false
  }
}
